import { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { downloadFile } from '@/lib/download'
import { Loader2, User, Download, Star, Clock, Package, Info, AlertCircle, Monitor, Smartphone, HelpCircle, Subtitles, ChevronDown, ChevronUp, Settings2, Mic2 } from 'lucide-react'
import { ReferenceImageSelector } from '@/components/generation/ReferenceImageSelector'
import { AssetPickerModal } from '@/components/library/AssetPickerModal'
import { DIGITAL_HUMAN_CREDITS, DigitalHumanAspectRatio, SpokenLanguage, SubtitleStyle, Asset } from '@/types'
import { useTasksStore, type TaskViewMode } from '@/stores/tasks'
import { embedRequirementId } from '@/lib/embedContext'

const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  font_color: '#8B5CF6',
  font_size: 12,
}

const SPOKEN_LANGUAGE_OPTIONS: Array<{ value: SpokenLanguage; label: string }> = [
  { value: 'auto', label: 'Auto (Use product language)' },
  { value: 'latin', label: 'English / Latin' },
  { value: 'zh_hans', label: '简体中文' },
  { value: 'zh_hant', label: '繁體中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'th', label: 'ไทย' },
]

const SUBTITLE_FONT_SIZE_OPTIONS = [12, 14, 16, 18, 20, 24, 28, 32, 36]
const SUBTITLE_OUTLINE_WIDTH_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8]
const AUTO_VOICE_ID = 'auto'

type DigitalHumanVoiceOption = {
  id: string
  name: string
  gender: 'female' | 'male'
  style: string
}

const DIGITAL_HUMAN_VOICE_OPTIONS: DigitalHumanVoiceOption[] = [
  {
    id: '02880d1c6fd94b7799d91135581ed810',
    name: 'Warm Female',
    gender: 'female',
    style: 'Warm and friendly, ideal for beauty, skincare, and lifestyle products.',
  },
  {
    id: '084760b4922a44599575c770070ec2d7',
    name: 'Confident Female',
    gender: 'female',
    style: 'Confident and elegant, ideal for fashion, luxury, and premium products.',
  },
  {
    id: '0ccb0bea067d4449ad367baeed7ea2e9',
    name: 'Energetic Female',
    gender: 'female',
    style: 'Energetic and youthful, ideal for fitness, food, and trend-driven products.',
  },
  {
    id: 'f38a635bee7a4d1f9b0a654a31d050d2',
    name: 'Professional Male',
    gender: 'male',
    style: 'Professional and trustworthy, ideal for tech, finance, and business products.',
  },
  {
    id: '0516c2d857eb425c94e90b068241914e',
    name: 'Casual Male',
    gender: 'male',
    style: 'Casual and relatable, ideal for gaming, electronics, and everyday products.',
  },
  {
    id: '01f98ed43e6140349f47dbd37a416827',
    name: 'Dynamic Male',
    gender: 'male',
    style: 'Dynamic and energetic, ideal for sports, automotive, and outdoor products.',
  },
  {
    id: '662f98eef6fd4ff89faf9bc2485659eb',
    name: 'Steady Mom',
    gender: 'female',
    style: 'Calm, reliable, rational, and life-experienced female voice, age 35-45.',
  },
  {
    id: '4754e1ec667544b0bd18cdf4bec7d6a7',
    name: 'Sharp Queen',
    gender: 'female',
    style: 'Clear logic, incisive delivery, strong opinions, and memorable one-liners.',
  },
  {
    id: '6SgrjVTpNOtJHBGAFnzX',
    name: 'Real Local Sister',
    gender: 'female',
    style: 'Authentic regional/local flavor, age 30-35, with a lived-in product recommendation feel.',
  },
  {
    id: '6664c469256949f19aab13c83c6005a1',
    name: 'Impulsive Sister',
    gender: 'female',
    style: 'Fast-paced, emotional, assertive female voice with a strong personality.',
  },
  {
    id: '66da5fabd6b944b3a42f35aed9631ad2',
    name: 'Casual Young Man',
    gender: 'male',
    style: 'Youthful and casual male voice for product case stories and organic recommendations.',
  },
  {
    id: '65a5fc7e561b4914af87ce8a28b76bd2',
    name: 'Sunny Sister',
    gender: 'female',
    style: 'Gentle, pleasant young female voice, age 20-35.',
  },
  {
    id: '65709b3145f0423f9d70e20034788c69',
    name: 'Strong Male',
    gender: 'male',
    style: 'Strong, opinionated, decisive male voice with a clear point of view.',
  },
  {
    id: '67c4c78b01f3404dbfbe6ab3176ba0a3',
    name: 'Analytical Woman',
    gender: 'female',
    style: 'Rigorous, rational, cool-toned female voice for explanatory narration, age 30-35.',
  },
  {
    id: '76940a9adcd0490a9ce2cfe9a64a2664',
    name: 'Weathered Grandfather',
    gender: 'male',
    style: 'Aged, weathered male voice with a strong narrative quality.',
  },
  {
    id: 'd92994ae0de34b2e8659b456a2f388b8',
    name: 'Smoky Villain',
    gender: 'male',
    style: 'Low, smoky, brooding male voice with an antihero or villain feel, age 35-45.',
  },
  {
    id: 'd2f4f24783d04e22ab49ee8fdc3715e0',
    name: 'Gentleman',
    gender: 'male',
    style: 'Courteous and humble young male voice, age 20-30.',
  },
  {
    id: 'f8c69e517f424cafaecde32dde57096b',
    name: 'Quirky Charmer',
    gender: 'female',
    style: 'Articulate, quick-witted, playful, and quirky female voice.',
  },
  {
    id: '6eafa43fdc16437b8f5abe512cc2b3cf',
    name: 'Mature Woman',
    gender: 'female',
    style: 'Motherly, philosophical, experienced, calm, and reliable female voice, age 35-45.',
  },
  {
    id: '6HafnaqPci0RvF2XGKDm',
    name: 'Analyst',
    gender: 'male',
    style: 'Mature, calm, rational male voice suitable for a male lead.',
  },
  {
    id: '661bff782b7c4e8ba64c80f13f07edfa',
    name: 'Everyday Spokesman',
    gender: 'male',
    style: 'Natural, everyday, grounded middle-aged male voice.',
  },
  {
    id: '828b59f834fd4c7188da322b6d9b6c75',
    name: 'Cheerful Mature Man',
    gender: 'male',
    style: 'Mature, steady, cheerful male voice suitable for a fatherly role.',
  },
  {
    id: '68a63c5eb8304b4f92a97efea30c50f8',
    name: 'Kind Grandfather',
    gender: 'male',
    style: 'Elderly, raspy male voice with emotional warmth.',
  },
  {
    id: '6YpPjY9z1H8T8CTXqGBq',
    name: 'Ethnic Local Sister',
    gender: 'female',
    style: 'Regional/local flavor with a lived-in, authentic female presence, age 30-45.',
  },
  {
    id: '42d00d4aac5441279d8536cd6b52c53c',
    name: 'Lively Sister',
    gender: 'female',
    style: 'Energetic, chatty female voice with a gossip-friendly vibe.',
  },
  {
    id: '6556a2647c7744299fa9f22d281c8df3',
    name: 'Financial Advisor',
    gender: 'female',
    style: 'Confident, rigorous, logical, and rational female voice.',
  },
]

const FEMALE_VOICE_OPTIONS = DIGITAL_HUMAN_VOICE_OPTIONS.filter((voice) => voice.gender === 'female')
const MALE_VOICE_OPTIONS = DIGITAL_HUMAN_VOICE_OPTIONS.filter((voice) => voice.gender === 'male')

function getAutoOutlineColor(fontColor: string): string {
  const normalized = fontColor.trim().toUpperCase()
  const red = Number.parseInt(normalized.slice(1, 3), 16)
  const green = Number.parseInt(normalized.slice(3, 5), 16)
  const blue = Number.parseInt(normalized.slice(5, 7), 16)
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255

  if (luminance >= 0.68 || (red >= 220 && green >= 180)) {
    return '#111111'
  }
  return '#FFFFFF'
}

function getAutoOutlineWidth(fontSize: number): number {
  if (fontSize >= 25) {
    return 4
  }
  if (fontSize >= 19) {
    return 3
  }
  return 2
}

function getErrorMessage(error: unknown, fallback: string): string {
  const responseData = (error as { response?: { data?: { error?: { message?: string }; detail?: string } } })?.response?.data
  return responseData?.error?.message || responseData?.detail || (error instanceof Error ? error.message : fallback)
}

export function DigitalHumanPage() {
  const queryClient = useQueryClient()
  const [productId, setProductId] = useState<string>('')
  const [withBumpers, setWithBumpers] = useState(true)
  const [aspectRatio, setAspectRatio] = useState<DigitalHumanAspectRatio>('9:16')
  const [scriptSuggestion, setScriptSuggestion] = useState('')
  const [customSubtitleText, setCustomSubtitleText] = useState('')
  const [withSubtitles, setWithSubtitles] = useState(true)  // Enable subtitles by default
  const [spokenLanguage, setSpokenLanguage] = useState<SpokenLanguage>('auto')
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(AUTO_VOICE_ID)
  const [subtitleStyle, setSubtitleStyle] = useState<SubtitleStyle>(DEFAULT_SUBTITLE_STYLE)
  const [showSubtitleAdvanced, setShowSubtitleAdvanced] = useState(false)
  const [showAdvancedReferences, setShowAdvancedReferences] = useState(false)
  const [useProductReference, setUseProductReference] = useState(false)
  const [useHumanReference, setUseHumanReference] = useState(false)
  const [productReferenceAsset, setProductReferenceAsset] = useState<Asset | null>(null)
  const [productReferenceSource, setProductReferenceSource] = useState<'manual' | 'product'>('manual')
  const [humanReferenceAsset, setHumanReferenceAsset] = useState<Asset | null>(null)
  const [humanReferenceSource, setHumanReferenceSource] = useState<'manual' | 'product'>('manual')
  const [assetPickerTarget, setAssetPickerTarget] = useState<'product' | 'human' | null>(null)
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null)

  // Use global task store instead of local state
  const { tasks, addTask, updateTask, setTaskViewMode, startPolling, removeTask, isPolling } = useTasksStore()

  const digitalHumanTasks = useMemo(
    () => Object.values(tasks)
      .filter(task => task.type === 'digital_human')
      .sort((a, b) => b.createdAt - a.createdAt),
    [tasks]
  )
  const foregroundTasks = useMemo(
    () => digitalHumanTasks.filter((task) => task.viewMode !== 'background'),
    [digitalHumanTasks]
  )
  const backgroundTasks = useMemo(
    () => digitalHumanTasks.filter((task) => task.viewMode === 'background'),
    [digitalHumanTasks]
  )
  const currentTask = useMemo(() => {
    if (focusedTaskId) {
      const focusedTask = foregroundTasks.find((task) => task.id === focusedTaskId)
      if (focusedTask) {
        return focusedTask
      }
    }
    return foregroundTasks[0]
  }, [focusedTaskId, foregroundTasks])

  useEffect(() => {
    if (focusedTaskId && !foregroundTasks.some((task) => task.id === focusedTaskId)) {
      setFocusedTaskId(null)
    }
  }, [focusedTaskId, foregroundTasks])

  // Resume polling if there's a processing task when component mounts
  // Note: Don't poll for 'pending' tasks - they are local placeholders waiting for API response
  useEffect(() => {
    digitalHumanTasks.forEach((task) => {
      if (
        (task.status === 'processing' || task.status === 'pending') &&
        !task.id.startsWith('pending-') &&
        !isPolling(task.id)
      ) {
        console.log('[DigitalHumanPage] Resuming polling for task:', task.id)
        startPolling(task.id)
      }
    })
  }, [digitalHumanTasks, startPolling, isPolling])

  // Refresh subscription when task completes (to update credits display)
  useEffect(() => {
    if (digitalHumanTasks.some((task) => task.status === 'completed' || task.status === 'failed')) {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    }
  }, [digitalHumanTasks, queryClient])

  // Notify hui parent frame when videos are ready (once per task)
  const notifiedDigitalHumanTaskIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (window.self === window.top) return
    const newlyCompleted = digitalHumanTasks.filter(
      (task) => task.status === 'completed' && task.result?.[0]?.url && !notifiedDigitalHumanTaskIdsRef.current.has(task.id),
    )
    if (newlyCompleted.length === 0) return
    newlyCompleted.forEach((task) => notifiedDigitalHumanTaskIdsRef.current.add(task.id))
    window.parent.postMessage({
      type: 'ASSETS_GENERATED',
      assets: newlyCompleted.map((task) => ({
        url: task.result![0].url,
        media_type: 'video',
        first_frame_url: task.result![0].first_frame_url || null,
      })),
      requirement_id: embedRequirementId,
    }, '*')
  }, [digitalHumanTasks])

  // Derived state from currentTask
  const taskStatus = currentTask?.status || null
  const isTaskRunning = foregroundTasks.some((task) => task.status === 'pending' || task.status === 'processing')
  const taskError = currentTask?.error || null
  const progressStage = currentTask?.progressStage || (isTaskRunning ? 'script' : null)
  const generatedVideo = currentTask?.result?.[0] ? {
    id: currentTask.result[0].id,
    url: currentTask.result[0].url || '',
    thumbnail: currentTask.result[0].url || '',
    duration: currentTask.metadata?.withBumpers ? 23 : 15,
  } : null

  const handleGeneratedVideoDownload = async () => {
    if (!generatedVideo?.id) return

    try {
      const result = await api.recordAssetDownload(generatedVideo.id)
      if (!result.download_url) {
        toast({ title: 'Download unavailable', variant: 'destructive' })
        return
      }
      downloadFile(result.download_url)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  // Get products for selection (required)
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
  })
  const selectedProduct = products?.items?.find((product) => product.id === productId) || null

  // Get credits
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.getSubscription(),
  })

  const autoInitializedProductReferenceRef = useRef<string | null>(null)
  const previousProductIdRef = useRef<string>('')

  useEffect(() => {
    const previousProductId = previousProductIdRef.current
    if (previousProductId && previousProductId !== productId) {
      autoInitializedProductReferenceRef.current = null
      if (productReferenceSource === 'manual') {
        setProductReferenceAsset(null)
        setProductReferenceSource('manual')
      }
    }

    if (!productId) {
      autoInitializedProductReferenceRef.current = null
      if (productReferenceSource === 'product') {
        setProductReferenceAsset(null)
        setProductReferenceSource('manual')
      }
    }

    previousProductIdRef.current = productId
  }, [productId, productReferenceSource])

  useEffect(() => {
    if (!showAdvancedReferences || !withBumpers || !productId || !selectedProduct?.screenshot_url) {
      return
    }
    if (autoInitializedProductReferenceRef.current === productId) {
      return
    }

    autoInitializedProductReferenceRef.current = productId
    setUseProductReference(true)
  }, [productId, selectedProduct?.screenshot_url, showAdvancedReferences, withBumpers])

  useEffect(() => {
    if (!productId) {
      return
    }

    if (!useProductReference) {
      if (productReferenceSource === 'product' && !selectedProduct?.screenshot_url) {
        setProductReferenceAsset(null)
        setProductReferenceSource('manual')
      }
      return
    }

    if (!selectedProduct?.screenshot_url) {
      if (productReferenceSource === 'product') {
        setProductReferenceAsset(null)
        setProductReferenceSource('manual')
      }
      return
    }

    const nextReferenceId = `product-screenshot-${selectedProduct.id}`
    if (
      productReferenceSource === 'product' &&
      productReferenceAsset?.id === nextReferenceId &&
      productReferenceAsset.url === selectedProduct.screenshot_url
    ) {
      return
    }

    if (!productReferenceAsset || productReferenceSource === 'product') {
      setProductReferenceAsset({
        id: nextReferenceId,
        asset_type: 'image',
        source_type: 'screenshot',
        url: selectedProduct.screenshot_url,
        download_url: null,
        file_name: 'Product Screenshot',
        description: null,
        score: null,
        is_favorited: false,
        created_at: selectedProduct.created_at,
        product_id: selectedProduct.id,
        parent_asset_id: null,
      })
      setProductReferenceSource('product')
    }
  }, [
    productId,
    productReferenceAsset?.id,
    productReferenceAsset?.url,
    productReferenceSource,
    selectedProduct,
    useProductReference,
  ])

  const resolvedProductReferenceAssetId = productReferenceAsset?.id?.startsWith('product-screenshot-')
    ? undefined
    : productReferenceAsset?.id
  const shouldUseProductReference = showAdvancedReferences && withBumpers && useProductReference
  const resolvedDigitalHumanReferenceAssetId = showAdvancedReferences && useHumanReference
    ? humanReferenceAsset?.id
    : undefined
  const hasCustomSubtitleText = customSubtitleText.trim().length > 0
  const selectedVoiceOption = selectedVoiceId === AUTO_VOICE_ID
    ? null
    : DIGITAL_HUMAN_VOICE_OPTIONS.find((voice) => voice.id === selectedVoiceId) || null

  const startDigitalHumanGeneration = async (viewMode: TaskViewMode) => {
    if (!productId) {
      return
    }

    if (viewMode === 'foreground' && currentTask && (currentTask.status === 'completed' || currentTask.status === 'failed')) {
      removeTask(currentTask.id)
    }

    const requestKey = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const pendingId = `pending-${requestKey}`
    const metadata = {
      withBumpers,
      aspectRatio,
      productId,
      spokenLanguage,
      voiceId: selectedVoiceId === AUTO_VOICE_ID ? null : selectedVoiceId,
      voiceName: selectedVoiceOption?.name || null,
      withSubtitles,
      hasCustomSubtitleText,
      subtitleStyle,
      showAdvancedReferences,
      useProductReference: shouldUseProductReference,
      useHumanReference: !!resolvedDigitalHumanReferenceAssetId,
    }

    addTask({
      id: pendingId,
      type: 'digital_human',
      status: 'pending',
      viewMode,
      metadata,
    })

    if (viewMode === 'foreground') {
      setFocusedTaskId(pendingId)
    }

    try {
      const data = await api.generateDigitalHuman({
        product_id: productId,
        with_bumpers: withBumpers,
        aspect_ratio: aspectRatio,
        spoken_language: spokenLanguage === 'auto' ? undefined : spokenLanguage,
        voice_id: selectedVoiceId === AUTO_VOICE_ID ? undefined : selectedVoiceId,
        script_suggestion: scriptSuggestion || undefined,
        custom_subtitle_text: hasCustomSubtitleText ? customSubtitleText : undefined,
        use_product_reference_image: shouldUseProductReference,
        product_reference_asset_id: shouldUseProductReference ? resolvedProductReferenceAssetId : undefined,
        digital_human_reference_asset_id: resolvedDigitalHumanReferenceAssetId,
        with_subtitles: withSubtitles,
        subtitle_style: subtitleStyle,
        requirement_id: embedRequirementId ?? undefined,
      })

      const resolvedViewMode = useTasksStore.getState().getTask(pendingId)?.viewMode ?? viewMode
      removeTask(pendingId)

      addTask({
        id: data.id,
        type: 'digital_human',
        status: 'processing',
        viewMode: resolvedViewMode,
        metadata,
      })

      startPolling(data.id)

      if (resolvedViewMode === 'foreground') {
        setFocusedTaskId(data.id)
      } else {
        toast({
          title: 'Digital human task started in background',
          description: 'You can keep editing while it renders.',
        })
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to start generation')
      updateTask(pendingId, {
        status: 'failed',
        error: message,
      })

      if (viewMode === 'background') {
        toast({
          title: 'Background digital human start failed',
          description: message,
          variant: 'destructive',
        })
      }
    }
  }

  const handleGenerate = () => {
    void startDigitalHumanGeneration('foreground')
  }

  const handleGenerateInBackground = () => {
    void startDigitalHumanGeneration('background')
  }

  const handleSendCurrentTaskToBackground = () => {
    if (!currentTask) {
      return
    }

    setTaskViewMode(currentTask.id, 'background')
    setFocusedTaskId(null)
    toast({
      title: 'Digital human task moved to background',
      description: 'Progress will continue in the task tray and Tasks page.',
    })
  }

  const handleBringTaskToFront = (taskId: string) => {
    setTaskViewMode(taskId, 'foreground')
    setFocusedTaskId(taskId)
  }

  // Credits based on mode
  const creditsNeeded = withBumpers ? DIGITAL_HUMAN_CREDITS.COMPLETE : DIGITAL_HUMAN_CREDITS.MINIMAL
  const hasEnoughCredits = (subscription?.credits_available || 0) >= creditsNeeded
  const canGenerate = productId && hasEnoughCredits

  // Duration estimate
  const estimatedDuration = withBumpers ? '≈30s' : '≈20s'
  const effectiveOutlineColor = subtitleStyle.outline_color ?? getAutoOutlineColor(subtitleStyle.font_color)
  const effectiveOutlineWidth = subtitleStyle.outline_width ?? getAutoOutlineWidth(subtitleStyle.font_size)
  const isProductReferenceDisabled = !withBumpers
  const activeAssetPickerTitle = assetPickerTarget === 'product'
    ? 'Select Product Reference Image'
    : 'Select Digital Human Reference Image'

  const updateSubtitleStyle = <K extends keyof SubtitleStyle>(key: K, value: SubtitleStyle[K]) => {
    setSubtitleStyle((current) => ({
      ...current,
      [key]: value,
    }))
  }

  const clearSubtitleStyleOverride = (key: 'outline_color' | 'outline_width') => {
    setSubtitleStyle((current) => {
      const next: SubtitleStyle = { ...current }
      delete next[key]
      return next
    })
  }

  return (
    <div className="space-y-6">
      {/* Main Grid: Left (1/3), Right (2/3) - matches VideoFreestylePage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left: Configuration (1/3 width) */}
        <div className="lg:col-span-1 space-y-6">
          {/* Product Selection (Required) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Select Product <span className="text-red-500">*</span>
            </label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              disabled={productsLoading || isTaskRunning}
            >
              <option value="">Select a product...</option>
              {products?.items?.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Product info will be used to generate the script automatically.
            </p>
          </div>

          {/* Aspect Ratio Selection */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-medium mb-3 text-sm">Aspect Ratio</h3>
            <div className="grid grid-cols-2 gap-3">
              {/* Vertical 9:16 */}
              <label
                className={`flex flex-col items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  aspectRatio === '9:16'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="aspectRatio"
                  checked={aspectRatio === '9:16'}
                  onChange={() => setAspectRatio('9:16')}
                  className="sr-only"
                  disabled={isTaskRunning}
                />
                <Smartphone className="w-6 h-6 mb-1" />
                <span className="font-medium text-sm">9:16</span>
                <span className="text-xs text-gray-500">Vertical</span>
              </label>

              {/* Horizontal 16:9 */}
              <label
                className={`flex flex-col items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  aspectRatio === '16:9'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="aspectRatio"
                  checked={aspectRatio === '16:9'}
                  onChange={() => setAspectRatio('16:9')}
                  className="sr-only"
                  disabled={isTaskRunning}
                />
                <Monitor className="w-6 h-6 mb-1" />
                <span className="font-medium text-sm">16:9</span>
                <span className="text-xs text-gray-500">Horizontal</span>
              </label>
            </div>
          </div>

          {/* Video Mode Selection */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-medium mb-3 text-sm">Video Mode</h3>
            <div className="space-y-2">
              {/* Complete Mode */}
              <label
                className={`flex items-start justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                  withBumpers
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="mode"
                    checked={withBumpers}
                    onChange={() => setWithBumpers(true)}
                    className="mt-0.5"
                    disabled={isTaskRunning}
                  />
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="font-medium text-sm">Complete Mode</p>
                      {/* Tooltip */}
                      <div className="relative group">
                        <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                          <div className="space-y-1">
                            <p>• Pre-roll: Hook video based on pain points</p>
                            <p>• Middle: Digital human explains product</p>
                            <p>• Post-roll: CTA for conversion</p>
                          </div>
                          <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Pre-roll + Digital Human + Post-roll
                    </p>
                  </div>
                </div>
                <span className="text-primary font-medium text-sm whitespace-nowrap">
                  {DIGITAL_HUMAN_CREDITS.COMPLETE} credits
                </span>
              </label>

              {/* Minimal Mode */}
              <label
                className={`flex items-start justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                  !withBumpers
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="mode"
                    checked={!withBumpers}
                    onChange={() => setWithBumpers(false)}
                    className="mt-0.5"
                    disabled={isTaskRunning}
                  />
                  <div>
                    <p className="font-medium text-sm">Minimal Mode</p>
                    <p className="text-xs text-gray-500">
                      Digital Human only
                    </p>
                  </div>
                </div>
                <span className="text-primary font-medium text-sm whitespace-nowrap">
                  {DIGITAL_HUMAN_CREDITS.MINIMAL} credits
                </span>
              </label>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <button
              type="button"
              className="flex w-full items-center justify-between text-left"
              onClick={() => setShowAdvancedReferences((open) => !open)}
              disabled={isTaskRunning}
            >
              <div>
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <Settings2 className="h-4 w-4" />
                  Advanced options
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Choose an optional voice and add media reference images.
                </p>
              </div>
              {showAdvancedReferences ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>

            {showAdvancedReferences && (
              <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900">
                    <Mic2 className="h-4 w-4" />
                    Voice
                  </label>
                  <select
                    value={selectedVoiceId}
                    onChange={(e) => setSelectedVoiceId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={isTaskRunning}
                  >
                    <option value={AUTO_VOICE_ID}>Auto (AI selects best match)</option>
                    <optgroup label="Female voices">
                      {FEMALE_VOICE_OPTIONS.map((voice) => (
                        <option key={voice.id} value={voice.id}>
                          {voice.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Male voices">
                      {MALE_VOICE_OPTIONS.map((voice) => (
                        <option key={voice.id} value={voice.id}>
                          {voice.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="mt-2 text-xs leading-5 text-gray-500">
                    {selectedVoiceOption
                      ? selectedVoiceOption.style
                      : 'AI will choose the voice from the available HeyGen catalog based on persona, product, and language.'}
                  </p>
                </div>

                <div className={`rounded-xl border p-4 ${
                  isProductReferenceDisabled ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'
                }`}>
                  <label className={`flex items-center justify-between gap-3 ${
                    isProductReferenceDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                  }`}>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Product reference image</p>
                      <p className="mt-1 text-xs text-gray-500">
                        Uses the selected product screenshot by default and only affects the post-roll segment.
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={useProductReference}
                        onChange={(e) => setUseProductReference(e.target.checked)}
                        className="sr-only peer"
                        disabled={isTaskRunning || isProductReferenceDisabled}
                      />
                      <div className="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/20 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:shadow-sm after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-transparent" />
                    </div>
                  </label>

                  {useProductReference && (
                    <div className="mt-4">
                      <ReferenceImageSelector
                        selectedAsset={productReferenceAsset}
                        onSelect={() => setAssetPickerTarget('product')}
                        onClear={() => {
                          setProductReferenceAsset(null)
                          setProductReferenceSource('manual')
                        }}
                        source={productReferenceSource}
                        disabled={isTaskRunning || isProductReferenceDisabled}
                        label="Product reference image"
                        emptyTitle="Add product reference image"
                        emptyDescription="Choose a product image from the library or upload a new one"
                        helperText="Preserves product appearance for the post-roll only."
                        selectedHelperText="Used only for the post-roll segment in Complete Mode."
                      />
                    </div>
                  )}

                  <p className="mt-3 text-xs text-gray-500">
                    {isProductReferenceDisabled
                      ? 'Only used for post-roll in Complete Mode.'
                      : 'Pre-roll never uses the product reference image.'}
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <label className={`flex items-center justify-between gap-3 ${
                    isTaskRunning ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                  }`}>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Digital human reference image</p>
                      <p className="mt-1 text-xs text-gray-500">
                        Guides the digital human first frame to keep the person’s core features closer to the reference.
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={useHumanReference}
                        onChange={(e) => setUseHumanReference(e.target.checked)}
                        className="sr-only peer"
                        disabled={isTaskRunning}
                      />
                      <div className="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/20 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:shadow-sm after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-transparent" />
                    </div>
                  </label>

                  {useHumanReference && (
                    <div className="mt-4">
                      <ReferenceImageSelector
                        selectedAsset={humanReferenceAsset}
                        onSelect={() => setAssetPickerTarget('human')}
                        onClear={() => {
                          setHumanReferenceAsset(null)
                          setHumanReferenceSource('manual')
                        }}
                        source={humanReferenceSource}
                        disabled={isTaskRunning}
                        label="Digital human reference image"
                        emptyTitle="Add digital human reference image"
                        emptyDescription="Choose a portrait-style image from the library or upload a new one"
                        helperText="Only affects the digital human segment first frame."
                        selectedHelperText="Used only to guide the digital human first frame before HeyGen rendering."
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Subtitles Toggle */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Spoken Language
            </label>
            <select
              value={spokenLanguage}
              onChange={(e) => setSpokenLanguage(e.target.value as SpokenLanguage)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              disabled={isTaskRunning}
            >
              {SPOKEN_LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              Controls the generated speech, voiceover language, and burned subtitles.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-blue-100 p-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom subtitle text <span className="text-gray-400">(Optional, exact)</span>
            </label>
            <textarea
              rows={4}
              placeholder="Paste the exact text you want the digital human to say. If subtitles are enabled, the same text will be burned onto the video..."
              value={customSubtitleText}
              onChange={(e) => setCustomSubtitleText(e.target.value)}
              className="w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-primary focus:ring-2 focus:ring-primary/20 resize-y"
              disabled={isTaskRunning}
            />
            <p className="mt-2 text-xs leading-5 text-blue-700">
              If you fill this in, the final spoken script will use it exactly as entered. If subtitles are enabled,
              the burned subtitles will use the same exact text. No rewriting, translation, punctuation cleanup, or
              wording changes. For readability, burned subtitles may still be split into multiple timed subtitle
              segments.
            </p>
          </div>

          {/* Subtitles Toggle */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-medium mb-3 text-sm flex items-center gap-2">
              <Subtitles className="w-4 h-4" />
              Subtitles
            </h3>
            <label className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              withSubtitles
                ? 'border-primary bg-white'
                : 'border-gray-200 bg-white hover:border-gray-300'
            } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <div>
                <p className="text-sm font-medium">Add subtitles to video</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Burn subtitles onto the video. Exact spoken-text constraints are configured above.
                </p>
              </div>
              <div className="relative ml-3">
                <input
                  type="checkbox"
                  checked={withSubtitles}
                  onChange={(e) => {
                    setWithSubtitles(e.target.checked)
                    if (!e.target.checked) {
                      setShowSubtitleAdvanced(false)
                    }
                  }}
                  className="sr-only peer"
                  disabled={isTaskRunning}
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm peer-checked:bg-primary peer-checked:after:border-transparent"></div>
              </div>
            </label>

            {withSubtitles && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700"
                  onClick={() => setShowSubtitleAdvanced((open) => !open)}
                  disabled={isTaskRunning}
                >
                  <span>Advanced subtitle settings</span>
                  {showSubtitleAdvanced ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {showSubtitleAdvanced && (
                  <div className="border-t border-gray-200 px-4 py-4 space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Font color
                          </span>
                          <span className="text-[11px] font-medium text-gray-400">
                            {subtitleStyle.font_color.toUpperCase()}
                          </span>
                        </div>
                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                          <input
                            type="color"
                            value={subtitleStyle.font_color}
                            onChange={(e) => updateSubtitleStyle('font_color', e.target.value)}
                            className="h-7 w-8 cursor-pointer rounded border border-gray-200 bg-transparent p-0"
                            disabled={isTaskRunning}
                          />
                          <span className="font-mono text-sm font-medium text-gray-700">
                            {subtitleStyle.font_color.toUpperCase()}
                          </span>
                        </label>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Font size
                          </span>
                          <span className="text-[11px] font-medium text-gray-400">
                            {subtitleStyle.font_size}px
                          </span>
                        </div>
                        <label className="block">
                          <select
                            value={subtitleStyle.font_size}
                            onChange={(e) => updateSubtitleStyle('font_size', Number(e.target.value))}
                            className="h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                            disabled={isTaskRunning}
                          >
                            {SUBTITLE_FONT_SIZE_OPTIONS.map((size) => (
                              <option key={size} value={size}>
                                {size}px
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Outline color
                          </span>
                          <button
                            type="button"
                            onClick={() => clearSubtitleStyleOverride('outline_color')}
                            className="text-[11px] font-medium text-primary disabled:cursor-not-allowed disabled:text-gray-300"
                            disabled={isTaskRunning || subtitleStyle.outline_color === undefined}
                          >
                            Auto
                          </button>
                        </div>
                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                          <input
                            type="color"
                            value={effectiveOutlineColor}
                            onChange={(e) => updateSubtitleStyle('outline_color', e.target.value)}
                            className="h-7 w-8 cursor-pointer rounded border border-gray-200 bg-transparent p-0"
                            disabled={isTaskRunning}
                          />
                          <span className="font-mono text-sm font-medium text-gray-700">
                            {effectiveOutlineColor.toUpperCase()}
                          </span>
                        </label>
                        <p className="mt-2 text-[11px] leading-4 text-gray-500">
                          {subtitleStyle.outline_color === undefined ? 'Auto contrast' : 'Custom outline color'}
                        </p>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Outline width
                          </span>
                          <span className="text-[11px] font-medium text-gray-400">
                            {subtitleStyle.outline_width === undefined ? `Auto (${effectiveOutlineWidth}px)` : `${subtitleStyle.outline_width}px`}
                          </span>
                        </div>
                        <label className="block">
                          <select
                            value={subtitleStyle.outline_width === undefined ? 'auto' : String(subtitleStyle.outline_width)}
                            onChange={(e) => {
                              if (e.target.value === 'auto') {
                                clearSubtitleStyleOverride('outline_width')
                                return
                              }
                              updateSubtitleStyle('outline_width', Number(e.target.value))
                            }}
                            className="h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                            disabled={isTaskRunning}
                          >
                            <option value="auto">Auto ({effectiveOutlineWidth}px)</option>
                            {SUBTITLE_OUTLINE_WIDTH_OPTIONS.map((size) => (
                              <option key={size} value={size}>
                                {size}px
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            )}
          </div>

          {/* Script Suggestion (Optional) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Script Suggestion <span className="text-gray-400">(Optional)</span>
            </label>
            <textarea
              rows={3}
              placeholder="Enter any suggestions for the AI..."
              value={scriptSuggestion}
              onChange={(e) => setScriptSuggestion(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-sm"
              disabled={isTaskRunning}
            />
            <div className="flex items-start gap-2 mt-2 p-2 bg-blue-50 rounded-lg">
              <Info className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">
                {hasCustomSubtitleText
                  ? `Custom subtitle text will be used exactly for the spoken script${withSubtitles ? '; burned subtitles will use the same text and may be split into timed cues for readability' : ''}. Script Suggestion only helps with visual planning, avatar${selectedVoiceOption ? '' : ', and voice'}.`
                  : selectedVoiceOption
                    ? `AI will automatically generate script and avatar based on your product, using ${selectedVoiceOption.name} as the voice.`
                    : 'AI will automatically generate script, avatar, and voice based on your product. If you include intended subtitle wording here, it will try to stay close to it.'}
              </p>
            </div>
          </div>

          {/* Generate Button */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3 text-sm">
              <div>
                <p className="text-gray-500">Duration</p>
                <p className="font-medium">{estimatedDuration}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500">Credits</p>
                <p className="font-medium text-primary">{creditsNeeded}</p>
              </div>
            </div>

            {/* Duration estimate warning */}
            <div className="flex items-start gap-2 mb-3 p-2 bg-amber-50 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Duration is estimated. Actual length depends on AI-generated script.
              </p>
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleGenerate}
                disabled={!canGenerate || isTaskRunning}
                className="w-full"
              >
                {isTaskRunning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <User className="w-4 h-4 mr-2" />
                )}
                Generate ({creditsNeeded} Credits)
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleGenerateInBackground}
                disabled={!canGenerate || isTaskRunning}
                className="w-full"
              >
                <User className="w-4 h-4 mr-2" />
                Generate in Background
              </Button>

              {isTaskRunning && currentTask && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSendCurrentTaskToBackground}
                  className="w-full"
                >
                  Send Current Task to Background
                </Button>
              )}
            </div>

            <p className="mt-3 text-xs text-gray-500">
              Background runs keep polling while you continue editing this page.
            </p>

            {!productId && (
              <p className="text-amber-600 text-xs text-center mt-2">
                Please select a product to continue.
              </p>
            )}

            {productId && !hasEnoughCredits && (
              <p className="text-red-500 text-xs text-center mt-2">
                Not enough credits. You have {subscription?.credits_available || 0}.
              </p>
            )}
          </div>
        </div>

        {/* Right: Preview (2/3 width) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-6">
            <h3 className="font-medium mb-4">Preview</h3>

            {backgroundTasks.length > 0 && (
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Background tasks</p>
                    <p className="text-xs text-slate-500">
                      Bring one back to the preview when you want to inspect it.
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-medium text-slate-600">
                    {backgroundTasks.length} active
                  </span>
                </div>
                <div className="mt-3 space-y-2">
                  {backgroundTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {task.metadata?.withBumpers ? 'Complete mode' : 'Minimal mode'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {task.status === 'pending'
                            ? 'Starting request...'
                            : task.status === 'processing'
                              ? 'Rendering in progress'
                              : task.status === 'failed'
                                ? 'Failed'
                                : 'Ready'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleBringTaskToFront(task.id)}
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isTaskRunning ? (
              <div className={`flex flex-col items-center justify-center py-12 gap-4 bg-gray-50 rounded-lg ${
                aspectRatio === '9:16' ? 'aspect-[9/16] max-w-[300px] mx-auto' : 'aspect-video'
              }`}>
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <div className="text-center">
                  <p className="text-gray-700 font-medium">Generating video...</p>
                  <p className="text-xs text-gray-500 mt-1">This may take a few minutes</p>

                  {/* Progress stage indicators */}
                  <div className="mt-4 space-y-2">
                    <div className={`flex items-center justify-center gap-2 text-xs transition-opacity ${
                      progressStage === 'script' ? 'text-primary font-medium' : progressStage ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {progressStage && progressStage !== 'script' ? (
                        <span className="text-green-600">✓</span>
                      ) : progressStage === 'script' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <span>○</span>
                      )}
                      <span>Generating script</span>
                    </div>

                    <div className={`flex items-center justify-center gap-2 text-xs transition-opacity ${
                      progressStage === 'images' ? 'text-primary font-medium' : progressStage === 'videos' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {progressStage === 'videos' ? (
                        <span className="text-green-600">✓</span>
                      ) : progressStage === 'images' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <span>○</span>
                      )}
                      <span>Generating reference images</span>
                    </div>

                    <div className={`flex items-center justify-center gap-2 text-xs transition-opacity ${
                      progressStage === 'videos' ? 'text-primary font-medium' : 'text-gray-400'
                    }`}>
                      {progressStage === 'videos' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <span>○</span>
                      )}
                      <span>Generating videos</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : taskStatus === 'failed' ? (
              <div className={`flex flex-col items-center justify-center py-12 gap-4 bg-red-50 rounded-lg ${
                aspectRatio === '9:16' ? 'aspect-[9/16] max-w-[300px] mx-auto' : 'aspect-video'
              }`}>
                <AlertCircle className="w-10 h-10 text-red-500" />
                <div className="text-center px-4">
                  <p className="text-red-700 font-medium mb-2">Generation Failed</p>
                  <p className="text-sm text-red-600">{taskError || 'An error occurred during video generation'}</p>
                  <Button
                    onClick={handleGenerate}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    disabled={!canGenerate}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : generatedVideo ? (
              <div className="space-y-4">
                <div className={`relative rounded-lg overflow-hidden bg-black ${
                  aspectRatio === '9:16' ? 'aspect-[9/16] max-w-[300px] mx-auto' : 'aspect-video'
                }`}>
                  <video
                    src={generatedVideo.url}
                    poster={generatedVideo.thumbnail}
                    controls
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    ~{generatedVideo.duration}s
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Star className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={handleGeneratedVideoDownload} disabled={!generatedVideo.url}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`flex flex-col items-center justify-center py-12 text-gray-400 bg-gray-50 rounded-lg ${
                aspectRatio === '9:16' ? 'aspect-[9/16] max-w-[300px] mx-auto' : 'aspect-video'
              }`}>
                <User className="w-12 h-12 mb-3" />
                <p className="text-sm text-center px-4">
                  Select a product and video mode to generate
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {selectedVoiceOption
                    ? `Voice: ${selectedVoiceOption.name}; avatar will be auto-selected by AI`
                    : 'Avatar and voice will be auto-selected by AI'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <AssetPickerModal
        open={assetPickerTarget !== null}
        onClose={() => setAssetPickerTarget(null)}
        onSelect={(asset) => {
          if (assetPickerTarget === 'product') {
            setProductReferenceAsset(asset)
            setProductReferenceSource('manual')
            setUseProductReference(true)
          } else if (assetPickerTarget === 'human') {
            setHumanReferenceAsset(asset)
            setHumanReferenceSource('manual')
            setUseHumanReference(true)
          }
        }}
        title={activeAssetPickerTitle}
        filterProductId={assetPickerTarget === 'product' ? productId || undefined : undefined}
        requirementId={embedRequirementId ?? undefined}
        assetType="image"
      />
    </div>
  )
}
